import Control from "../../../assets/vfx/controls/control";
import { IControlStyles, useControlStyles } from "../../../assets/layouts";
import React from "react";
import Form from "../../../assets/vfx/form/form";
import { Col } from "react-bootstrap";
import { SearchContainer } from "../../../assets/vfx/search/searchContainer";
import { SearchDto, SearchOperator } from "../../../assets/utils/lib/searchDto";
import { Constants } from "../../../assets/utils/lib/constants";
import { defaultSearchQuery, GRID_ACTION_TYPES } from "../../../assets/types";
import { ControlDataGridColumnProps } from "../../../assets/vfx/controls/components/datagrid/controlDataGrid";
import { QuestionService } from "./questionService";
import { QuestionViewDto, SectionDto } from "../../../services/dto";
import { QuestionLookupProps } from "./questionForm";
import {
  createActionColumn,
  createLayoutOptions,
} from "../../../assets/configuration/gridConfig";
import { QuestionnaireType } from "../questionnaireForm/questionnaireContainer";

interface QuestionnaireQuestionPageProps {
  questionnaireId?: string | undefined;
  sectionGridQuery?: SectionDto[] | undefined;
  controlStyles?: IControlStyles | undefined;
  questionnaireType?: string | undefined;
  onCustomEvent?: (actionType: string, dataId: string) => void;
  lookups: QuestionLookupProps | undefined;
}
interface QuestionSearch {
  name: string;
  questionTypeId: string;
}

const searchInitialValues: QuestionSearch = {
  name: Constants.emptyString,
  questionTypeId: Constants.emptyGuid,
};

const DATA_ID_FIELD = "id";

const createGridColumns = (): ControlDataGridColumnProps[] => [
  {
    data: "id",
    title: "ID",
    columnType: "number",
    visible: false,
  },
  {
    data: "name",
    title: "Name",
    columnType: "text",
    sortable: true,
    visible: true,
    width: "30%",
  },
  {
    data: "type",
    title: "Question Type",
    columnType: "text",
    sortable: true,
    visible: true,
  },
  {
    data: "section",
    title: "Section",
    columnType: "text",
    sortable: true,
    visible: true,
  },
];

export const QuestionPage = (props: QuestionnaireQuestionPageProps) => {
  const updatedSearchQuery: SearchDto = {
    ...defaultSearchQuery,
    filters: [
      ...defaultSearchQuery.filters,
      {
        field: "questionnaireId",
        operator: SearchOperator.Equal,
        value: props.questionnaireId,
      },
    ],
  };

  const [searchQuery, setSearchQuery] =
    React.useState<SearchDto>(updatedSearchQuery);

  const styles: IControlStyles = props?.controlStyles ?? useControlStyles();

  const questionGridQuery = QuestionService.gridQuery(searchQuery);
  const { data } = questionGridQuery;
  const isGridLoading = questionGridQuery.isLoading || questionGridQuery.isFetching;
  const questions: QuestionViewDto[] = data?.result ?? [];
  const hasData: boolean = questions.length > 0;
  const gridColumns = createGridColumns();
  const actionColumn = createActionColumn(styles, DATA_ID_FIELD, [
    GRID_ACTION_TYPES.EDIT,
    GRID_ACTION_TYPES.VIEW,
  ]);
  const layoutOptions = createLayoutOptions(hasData);
  const handleSearch = (values: QuestionSearch) => {
    const filters = [];

    if (values.name.trim() !== Constants.emptyString) {
      filters.push({
        field: "name",
        operator: SearchOperator.Contains,
        value: values.name,
      });
    }

    if (values.questionTypeId !== Constants.emptyGuid) {
      filters.push({
        field: "questionTypeId",
        operator: SearchOperator.Equal,
        value: values.questionTypeId,
      });
    }

    if (filters.length > 0 && Array.isArray(defaultSearchQuery.filters)) {
      filters.push(...defaultSearchQuery.filters);
    }

    if (filters.length > 0) {
      setSearchQuery({
        ...defaultSearchQuery,
        filters,
      });
    }
  };

  const handleClear = (): void => {
    setSearchQuery((prev) => ({
      ...defaultSearchQuery,
      sorts: prev.sorts,
      pageSize: prev.pageSize ?? 10,
    }));
  };
  
  const handleNew = () =>
    props?.onCustomEvent?.(GRID_ACTION_TYPES.NEW, Constants.emptyGuid);

  return (
    <>
      {props.questionnaireType != QuestionnaireType.Poll.toLowerCase() ? (
        <SearchContainer
          initialValues={searchInitialValues}
          onSearch={handleSearch}
          onNew={handleNew}
          onClear={handleClear}
          enableReinitialize
        >
          <Col lg={4}>
            <Form.Text name={"name"} label={"Name"} />
          </Col>
          <Col lg={4} className="ms-2">
            <Form.Dropdown
              name={"questionTypeId"}
              label={"Question Type"}
              blankOption={{
                key: Constants.emptyGuid,
                display: "--Select Questin Type--",
                value: Constants.emptyGuid,
              }}
              options={props?.lookups?.questionType}
            />
          </Col>
        </SearchContainer>
      ) : (
        !hasData && (
          <SearchContainer
            onNew={handleNew}
            enableReinitialize
          ></SearchContainer>
        )
      )}

      <Control.DataGrid
        dataId="id"
        cols={gridColumns}
        data={questions}
        actionCol={actionColumn}
        layoutOptions={layoutOptions}
        onCustomEvent={props?.onCustomEvent}
        loading={isGridLoading}
      />
    </>
  );
};
